import {
  useState,
  useMemo,
  useRef,
  useCallback,
  createContext,
  useContext
} from "react";
import { useDebouncedValue } from "shared/hooks/use-debounced-value";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { Copy01Icon } from "@hugeicons-pro/core-stroke-rounded";
import {
  Badge,
  BadgeSize,
  BadgeVariant,
  BaseInput,
  Button,
  ButtonSize,
  ButtonVariant,
  Select,
  Show
} from "shared/components";
import { components, MenuListProps } from "react-select";
import { SaveCancelButtons } from "shared/app/SaveCancelButtons";
import { Row } from "views/applications-id-report/components/Row";
import { SectionCard } from "shared/app/SectionCard";
import { Unit } from "types/landlord/address";
import addressApi from "api/landlord/address";
import {
  lockboxesApi,
  Lockbox,
  GeneratedLockboxCode
} from "api/landlord/lockboxes";
import { VerificationStatusPill } from "views/lockboxes/components/VerificationStatusPill";
import { getErrorMessage } from "utils/error";
import { handleCopy } from "utils/handleCopy";
import { useAuthContext } from "providers/Auth";
import { GenerateLockboxCodeModal } from "./GenerateLockboxCodeModal";

// ─── Types ────────────────────────────────────────────────────────────────────

type LockboxOption = { value: number; label: string };

// ─── Context ──────────────────────────────────────────────────────────────────

interface LockboxMenuContextValue {
  propertyOptions: LockboxOption[];
  unassignedOptions: LockboxOption[];
  selectedLockboxId: number | null;
  isLoading: boolean;
  onSelect: (opt: LockboxOption) => void;
}

const LockboxMenuContext = createContext<LockboxMenuContextValue | null>(null);

const useLockboxMenuContext = () => {
  const ctx = useContext(LockboxMenuContext);
  if (!ctx) throw new Error("Must be inside LockboxMenuContext.Provider");
  return ctx;
};

// ─── Custom MenuList (module-level, stable reference) ─────────────────────────

const SectionHeader = ({
  title,
  className = "pt-1"
}: {
  title: string;
  className?: string;
}) => (
  <div className={`px-4 ${className} pb-2`}>
    <span className="text-xs text-content-200">{title}</span>
    <div className="border-b border-alpha-40 mt-2" />
  </div>
);

const LockboxMenuItemRow = ({
  opt,
  isCurrent,
  onSelect
}: {
  opt: LockboxOption;
  isCurrent: boolean;
  onSelect: (opt: LockboxOption) => void;
}) => (
  <button
    type="button"
    className="w-full flex items-center justify-between gap-2 px-4 py-2.5 text-left cursor-pointer hover:bg-alpha-40"
    onClick={() => onSelect(opt)}
  >
    <span className="text-sm font-medium text-content-1000 truncate min-w-0">
      {opt.label}
    </span>
    {isCurrent && (
      <Badge
        variant={BadgeVariant.PRIMARY}
        size={BadgeSize.SM}
        className="shrink-0"
      >
        Current
      </Badge>
    )}
  </button>
);

const CustomMenuList = (props: MenuListProps<unknown>) => {
  const {
    propertyOptions,
    unassignedOptions,
    selectedLockboxId,
    isLoading,
    onSelect
  } = useLockboxMenuContext();

  const hasBothGroups =
    propertyOptions.length > 0 && unassignedOptions.length > 0;

  if (isLoading)
    return (
      <components.MenuList {...props}>
        <div className="px-4 py-2 text-center text-content-500">Loading...</div>
      </components.MenuList>
    );

  if (propertyOptions.length === 0 && unassignedOptions.length === 0)
    return (
      <components.MenuList {...props}>
        <div className="px-4 py-2 text-center text-content-500">
          No lockboxes found
        </div>
      </components.MenuList>
    );

  return (
    <components.MenuList {...props}>
      <div className="bg-background-1000 py-1">
        {propertyOptions.length > 0 && (
          <>
            {hasBothGroups && (
              <SectionHeader title="Assigned to this property" />
            )}
            {propertyOptions.map((opt) => (
              <LockboxMenuItemRow
                key={opt.value}
                opt={opt}
                isCurrent={opt.value === selectedLockboxId}
                onSelect={onSelect}
              />
            ))}
          </>
        )}
        {unassignedOptions.length > 0 && (
          <>
            <SectionHeader
              title="Unassigned"
              className={hasBothGroups ? "pt-3" : "pt-1"}
            />
            {unassignedOptions.map((opt) => (
              <LockboxMenuItemRow
                key={opt.value}
                opt={opt}
                isCurrent={opt.value === selectedLockboxId}
                onSelect={onSelect}
              />
            ))}
          </>
        )}
      </div>
    </components.MenuList>
  );
};

const lockboxSelectorComponents = { MenuList: CustomMenuList };

// ─── EditLockboxForm ──────────────────────────────────────────────────────────

const EditLockboxForm = ({
  unit,
  propertyId,
  onClose
}: {
  unit: Unit;
  propertyId: number;
  onClose: () => void;
}) => {
  const selectRef = useRef<{ blur: () => void } | null>(null);
  const [selectedLockboxId, setSelectedLockboxId] = useState<number | null>(
    unit.lockbox?.id ?? null
  );
  const [selectedOption, setSelectedOption] = useState<LockboxOption | null>(
    unit.lockbox ? { value: unit.lockbox.id, label: unit.lockbox.name } : null
  );

  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch] = useDebouncedValue(searchInput, 300);
  const queryClient = useQueryClient();

  const { data: availableLockboxes = [], isLoading } = useQuery({
    queryKey: [
      "LOCKBOXES",
      {
        available: true,
        property_id: propertyId,
        allow_no_property: true,
        search: debouncedSearch
      }
    ],
    queryFn: () =>
      lockboxesApi
        .getLockboxes({
          available: true,
          property_id: propertyId,
          allow_no_property: true,
          per_page: 100,
          search: debouncedSearch || undefined
        })
        .then((r) => r.items)
  });

  const propertyOptions = useMemo(() => {
    const fromQuery = availableLockboxes
      .filter((lb) => lb.property?.id === propertyId)
      .map((lb) => ({ value: lb.id, label: lb.name }));

    // currently-assigned lockbox has unit_id set → excluded by available: true, inject manually
    if (unit.lockbox && !fromQuery.find((o) => o.value === unit.lockbox!.id))
      return [
        { value: unit.lockbox.id, label: unit.lockbox.name },
        ...fromQuery
      ];

    return fromQuery;
  }, [availableLockboxes, propertyId, unit.lockbox]);

  const unassignedOptions = useMemo(
    () =>
      availableLockboxes
        .filter((lb) => !lb.property)
        .map((lb) => ({ value: lb.id, label: lb.name })),
    [availableLockboxes]
  );

  const allOptions = useMemo(() => {
    const available = [...propertyOptions, ...unassignedOptions];
    if (
      selectedOption &&
      !available.find((o) => o.value === selectedOption.value)
    )
      return [selectedOption, ...available];
    return available;
  }, [propertyOptions, unassignedOptions, selectedOption]);

  const selectedLockboxCode = useMemo(
    () =>
      availableLockboxes.find((lb) => lb.id === selectedLockboxId)?.code ??
      (unit.lockbox?.id === selectedLockboxId
        ? (unit.lockbox?.code ?? null)
        : null),
    [availableLockboxes, selectedLockboxId, unit.lockbox]
  );

  const handleSelect = useCallback((opt: LockboxOption) => {
    setSelectedLockboxId(opt.value);
    setSelectedOption(opt);
    selectRef.current?.blur();
  }, []);

  const contextValue = useMemo<LockboxMenuContextValue>(
    () => ({
      propertyOptions,
      unassignedOptions,
      selectedLockboxId,
      isLoading,
      onSelect: handleSelect
    }),
    [
      propertyOptions,
      unassignedOptions,
      selectedLockboxId,
      isLoading,
      handleSelect
    ]
  );

  const { mutate: save, isPending } = useMutation({
    mutationKey: ["UNIT", "UPDATE_LOCKBOX"],
    mutationFn: () =>
      addressApi.editUnit(unit.id, { lockbox_id: selectedLockboxId }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["unit", String(propertyId), String(unit.id)]
      });
      queryClient.invalidateQueries({
        queryKey: ["properties", String(propertyId)]
      });
      onClose();
      toast.success("Lockbox updated successfully");
    },
    onError: (err) => {
      toast.error(getErrorMessage(err));
    }
  });

  return (
    <SectionCard title="Lockbox">
      <div className="space-y-4">
        <div className="flex gap-4">
          <div className="flex-1">
            <LockboxMenuContext.Provider value={contextValue}>
              <Select
                ref={selectRef}
                id="lockbox"
                label="Lockbox"
                value={selectedLockboxId ?? ""}
                clearable
                hideOptional
                searchable
                options={allOptions}
                placeholder="Select lockbox"
                components={lockboxSelectorComponents}
                loading={isLoading}
                onInputChange={(val: string) => setSearchInput(val)}
                onChange={(e) => {
                  if (!Array.isArray(e) && !e.target.value) {
                    setSelectedLockboxId(null);
                    setSelectedOption(null);
                  }
                }}
              />
            </LockboxMenuContext.Provider>
          </div>
          <BaseInput
            id="admin_code"
            label="Admin code"
            className="flex-1"
            value={selectedLockboxCode ?? ""}
            onChange={() => {}}
            disabled
            hideOptional
          />
        </div>
        <SaveCancelButtons
          onSave={() => save()}
          onCancel={onClose}
          saveLoading={isPending}
          saveDisabled={isPending}
        />
      </div>
    </SectionCard>
  );
};

// ─── LockboxSection ───────────────────────────────────────────────────────────

const validityCaption = (
  result: GeneratedLockboxCode,
  propertyTimezone?: string | null
) => {
  if (!result.cancelable) return "Valid for the rest of today";
  const until = new Date(result.valid_until).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    ...(propertyTimezone
      ? { timeZone: propertyTimezone, timeZoneName: "short" }
      : {})
  });
  return `Valid until ${until}`;
};

// Caller keys this on lockbox id + vendor so a change remounts it with fresh state.
const LockboxCodeGenerator = ({
  lockbox,
  propertyTimezone
}: {
  lockbox: Lockbox;
  propertyTimezone?: string | null;
}) => {
  const [isConfirming, setIsConfirming] = useState(false);
  const [generatedCode, setGeneratedCode] =
    useState<GeneratedLockboxCode | null>(lockbox.active_on_demand_code);

  return (
    <>
      <div className="flex items-center justify-between gap-2.5 rounded-lg border border-dashed border-alpha-100 bg-background-900 p-6">
        <Show if={!generatedCode}>
          <div className="space-y-1">
            <p className="text-2xl font-bold text-content-1000">- - - -</p>
            <p className="text-sm font-medium text-content-400">
              The code will appear here
            </p>
          </div>
          <Button
            size={ButtonSize.SM}
            className="shrink-0"
            onClick={() => setIsConfirming(true)}
          >
            Generate lockbox code
          </Button>
        </Show>

        <Show if={!!generatedCode}>
          <div className="space-y-1">
            <p className="text-2xl font-bold text-content-1000">
              {generatedCode?.code}
            </p>
            <p className="text-sm font-medium text-content-400">
              {generatedCode &&
                validityCaption(generatedCode, propertyTimezone)}
            </p>
          </div>
          <div className="flex shrink-0 gap-2">
            <Button size={ButtonSize.SM} onClick={() => setIsConfirming(true)}>
              Re-generate code
            </Button>
            <Button
              size={ButtonSize.SM}
              HugeIcon={Copy01Icon}
              onClick={() => generatedCode && handleCopy(generatedCode.code)}
            >
              Copy code
            </Button>
          </div>
        </Show>
      </div>

      <GenerateLockboxCodeModal
        opened={isConfirming}
        onClose={() => setIsConfirming(false)}
        onGenerated={(result) => {
          setGeneratedCode(result);
          setIsConfirming(false);
        }}
        lockbox={lockbox}
      />
    </>
  );
};

export const LockboxSection = ({
  unit,
  propertyId,
  propertyTimezone
}: {
  unit: Unit;
  propertyId: number;
  propertyTimezone?: string | null;
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const { type, partner } = useAuthContext();

  const lockbox = unit.lockbox;

  const vendorConnected =
    (lockbox?.vendor === "sentrilock" && !!partner?.sentrilock_connected) ||
    (lockbox?.vendor === "codebox" && !!partner?.codebox_connected);

  const canGenerateCode =
    !!lockbox && type.isBoomCRM && type.isLockboxes && vendorConnected;

  if (isEditing)
    return (
      <EditLockboxForm
        unit={unit}
        propertyId={propertyId}
        onClose={() => setIsEditing(false)}
      />
    );

  return (
    <SectionCard
      title="Lockbox"
      headerExtra={
        <Button
          size={ButtonSize.SM}
          variant={ButtonVariant.BASE}
          onClick={() => setIsEditing(true)}
        >
          Edit
        </Button>
      }
    >
      <div className="space-y-4">
        <Row title="Lockbox" value={lockbox?.name ?? "--"} />
        {lockbox && (
          <Row
            title="Verification"
            value={
              <VerificationStatusPill status={lockbox.verification_status} />
            }
          />
        )}
        <Row title="Admin code" value={lockbox?.code ?? "--"} />

        {canGenerateCode && lockbox && (
          <LockboxCodeGenerator
            key={`${lockbox.id}:${lockbox.vendor}`}
            lockbox={lockbox}
            propertyTimezone={propertyTimezone}
          />
        )}
      </div>
    </SectionCard>
  );
};
